import { useState, useEffect } from 'react';

/**
 * Custom React hook to manage a stateful value synchronized with localStorage.
 * @param key The key under which the value is stored in localStorage.
 * @param initialValue The initial value to use if there is no stored value.
 * @returns A tuple containing the current stored value and a setter function.
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
  // Initialize state by reading from localStorage or falling back to initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    // localStorage is not available during server-side rendering, so return initialValue
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      // Attempt to get the stored JSON string from localStorage by key
      const item = window.localStorage.getItem(key);
      // Parse the JSON string into original value, or return initialValue if not found
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // Log any errors during parsing or retrieval and return initialValue as fallback
      console.error('useLocalStorage get error:', error);
      return initialValue;
    }
  });

  // Whenever the storedValue or key changes, persist the value to localStorage
  useEffect(() => {
    try {
      // Serialize the state as JSON and save it under the specified key
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      // Log any errors encountered while saving to localStorage
      console.error('useLocalStorage set error:', error);
    }
  }, [key, storedValue]);

  // Return the current stored value and the function to update it (like useState)
  return [storedValue, setStoredValue] as const;
}
